/**
 * El widget: orquesta carga, disponibilidad en vivo, selección con bloqueo en el servidor, temporizador,
 * y la interfaz (leyenda, mapa, avisos, ventana de zona de pie y barra inferior) dentro de un Shadow DOM.
 */
import { ApiError, createApi, type Api } from "./api.js";
import { Availability, BLOCKED, emptySnapshot, FREE, HELD, MINE, SOLD } from "./availability.js";
import { buildModel, inkFor, type ChartModel } from "./chart-model.js";
import { clear, h, icon, markPath, round1, s } from "./dom.js";
import { formatClock, formatMoney } from "./format.js";
import { createHoldStore, persistHold, recoverHold, type HoldStore } from "./hold-store.js";
import { blockNotice, messagesFor, normalizeLocale, type Messages } from "./i18n.js";
import { MapView } from "./map-view.js";
import { Poller } from "./poller.js";
import { buildSelection, clampQty, gaMaxQty, pickBestLocal } from "./selection.js";
import { getSessionId, isSessionToken } from "./session.js";
import { CSS } from "./styles.js";
import { HoldSync, toApiError, type HoldState, type SyncOp } from "./sync.js";
import type { EventConfig, HoldInfo, HoldView, Locale, Selection, SeatSel, SeatsOptions, SeatsWidget, Snapshot, WidgetError } from "./types.js";

type Kind = "info" | "warn" | "error";

const isMac = (): boolean => typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent || "");
const reduceMotion = (): boolean => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

let sheet: CSSStyleSheet | null | undefined;
function styleSheet(): CSSStyleSheet | null {
  if (sheet !== undefined) return sheet;
  try {
    sheet = new CSSStyleSheet();
    sheet.replaceSync(CSS);
  } catch {
    sheet = null;
  }
  return sheet;
}

export function toWidgetError(e: unknown, recovered = false): WidgetError {
  const err = toApiError(e);
  return { code: err.code, message: err.message, status: err.status || undefined, details: err.details, retryable: err.retryable, ...(recovered ? { recovered } : {}) };
}

export class Widget implements SeatsWidget {
  private readonly t: Messages;
  private readonly locale: Locale;
  private readonly max: number;
  private readonly holdOnSelect: boolean;
  private readonly host: HTMLDivElement;
  private readonly shadow: ShadowRoot;
  private readonly w: HTMLDivElement;
  private readonly live = h("div", { class: "sr", role: "status", "aria-live": "polite", "aria-atomic": "true" });
  private readonly alert = h("div", { class: "sr", role: "alert", "aria-live": "assertive", "aria-atomic": "true" });
  private readonly life = new AbortController();
  private readonly api: Api;
  private readonly sessionId: string;
  private sessionToken: string | undefined;
  /** ya se avisó con `onError` del bloqueo por sesión actual (se rearma con `setSessionToken`) */
  private lockReported = false;
  private readonly holdStore: HoldStore;
  private readonly persist: (hold: HoldState | null) => void;
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();
  private readonly cleanups: Array<() => void> = [];

  private cfg: EventConfig | null = null;
  private model: ChartModel | null = null;
  private av: Availability | null = null;
  private map: MapView | null = null;
  private sync: HoldSync | null = null;
  private poller: Poller | null = null;
  private offset = 0;
  private destroyed = false;
  private forceFresh = false;
  private offline = false;
  private verifying = false;
  private warnedExpiry = 0;
  private lastSig = "";
  private lastW = 0;
  private hintAt = 0;
  private tickTimer: ReturnType<typeof setInterval> | null = null;

  // piezas de interfaz
  private mapBox!: HTMLDivElement;
  private tip!: HTMLDivElement;
  private tipVisible = false;
  private hint!: HTMLDivElement;
  private note!: HTMLDivElement;
  private noteText!: HTMLSpanElement;
  private noteIcon!: HTMLSpanElement;
  private noteTimer: ReturnType<typeof setTimeout> | null = null;
  private pop!: HTMLDivElement;
  private popZone = -1;
  private sum!: HTMLDivElement;
  private tm!: HTMLDivElement;
  private clk!: HTMLElement;
  private extendBtn!: HTMLButtonElement;
  private sy!: HTMLSpanElement;
  private zIn!: HTMLButtonElement;
  private zOut!: HTMLButtonElement;
  private zReset!: HTMLButtonElement;
  private catBtns: HTMLButtonElement[] = [];
  private hl = -1;
  private extending = false;

  constructor(private readonly o: SeatsOptions, baseUrl: string) {
    this.locale = normalizeLocale(o.locale);
    this.t = messagesFor(this.locale);
    this.max = Math.max(1, Math.floor(o.maxSelectable ?? 8));
    this.holdOnSelect = o.holdOnSelect !== false;
    this.sessionId = getSessionId();
    this.sessionToken = o.sessionToken || undefined;
    this.holdStore = createHoldStore(o.eventId);
    this.persist = persistHold(this.holdStore);
    this.api = createApi({ baseUrl, key: o.publicKey, sessionId: this.sessionId, sessionToken: () => this.sessionToken, signal: this.life.signal });

    this.host = h("div", { "data-seats-ai": "" });
    this.shadow = this.host.attachShadow({ mode: "open" });
    const ss = styleSheet();
    if (ss) this.shadow.adoptedStyleSheets = [ss];
    else this.shadow.appendChild(h("style", undefined, CSS));
    this.w = h("div", { class: "w" });
    this.shadow.appendChild(this.w);
    this.applyTheme();
    o.el.appendChild(this.host);
    void this.load();
  }

  /* ------------------------------ tema ------------------------------------ */

  private applyTheme(): void {
    const th = this.o.theme ?? {};
    const st = this.host.style;
    if (th.accent) {
      st.setProperty("--seats-accent", th.accent);
      if (/^#[0-9a-f]{6}$/i.test(th.accent)) st.setProperty("--seats-on-accent", inkFor(th.accent));
    }
    if (th.radius) st.setProperty("--seats-radius", th.radius);
    if (th.font) st.setProperty("--seats-font", th.font);
    const apply = (dark: boolean): void => {
      this.w.toggleAttribute("data-dark", dark);
    };
    if (th.mode === "dark" || th.mode === "light") return apply(th.mode === "dark");
    if (typeof matchMedia === "function") {
      const mq = matchMedia("(prefers-color-scheme: dark)");
      apply(mq.matches);
      const fn = (e: MediaQueryListEvent) => apply(e.matches);
      mq.addEventListener?.("change", fn);
      this.cleanups.push(() => mq.removeEventListener?.("change", fn));
    }
  }

  /* ------------------------------ carga ----------------------------------- */

  private setOffset(serverTime: number, sentAt?: number): void {
    const ref = sentAt === undefined ? Date.now() : (sentAt + Date.now()) / 2;
    this.offset = serverTime - ref;
  }

  private serverNow = (): number => Date.now() + this.offset;

  private show(...kids: Node[]): void {
    clear(this.w);
    this.w.append(...kids);
  }

  private async load(): Promise<void> {
    const t = this.t;
    this.show(h("div", { class: "ld", role: "status" }, h("div", { class: "sk" }), h("div", undefined, t.loading)));
    try {
      const sentAt = Date.now();
      const cfg = await this.api.event(this.o.eventId);
      this.setOffset(cfg.serverTime, sentAt);
      const loaded = await Promise.all([this.api.chart(cfg.chartUrl), this.api.snapshot(cfg.availabilityUrl), this.recover(cfg)]);
      const snap = loaded[1] ?? emptySnapshot(); // sin foto publicada = todo libre
      if (this.destroyed) return;
      const model = buildModel(loaded[0]);
      if (model.seats.length === 0 && model.zones.length === 0) throw new ApiError("invalid_chart", "El plano no contiene butacas", 0);
      this.cfg = cfg;
      this.mount(cfg, model, snap, loaded[2]);
    } catch (e) {
      if (this.destroyed) return;
      const err = toApiError(e);
      if (err.code === "aborted") return;
      const msg = err.status === 401 || err.status === 403 ? t.authError : err.status === 404 ? t.notFoundError : t.loadError;
      const retry = h("button", { type: "button", class: "btn" }, t.retry);
      retry.addEventListener("click", () => void this.load());
      this.show(h("div", { class: "ld err", role: "alert" }, h("div", { class: "em" }, msg), retry));
      this.o.onError?.(toWidgetError(err));
    }
  }

  /* ------------------------------ montaje --------------------------------- */

  /**
   * ¿Quedó un bloqueo mío de antes de recargar? Solo si el widget bloquea en el servidor y, si el evento exige sesión del
   * portal, hay token. Va en paralelo con el plano y la foto y nunca falla (ver `recoverHold`).
   */
  private async recover(cfg: EventConfig): Promise<HoldView | null> {
    if (!this.holdOnSelect || (cfg.requireSession === true && !this.sessionToken)) return null;
    const v = await recoverHold({ store: this.holdStore, api: this.api.holds(cfg.eventId), eventId: cfg.eventId, now: this.serverNow });
    return v && { ...v, serverTime: undefined }; // el desfase de reloj ya se midió con la configuración del evento
  }

  private mount(cfg: EventConfig, model: ChartModel, snap: Snapshot, restored: HoldView | null): void {
    const t = this.t;
    this.model = model;
    const av = (this.av = new Availability(model.index, model.seats.length));
    av.apply(snap, this.serverNow());

    this.sync = new HoldSync(
      this.api.holds(cfg.eventId),
      {
        selectionChanged: () => this.afterSelection(),
        holdChanged: (hd) => this.onHoldChanged(hd),
        serverTime: (ts) => this.setOffset(ts),
        released: (ids, st) => {
          for (const id of ids) {
            const i = model.index.get(id);
            if (i !== undefined && st) av.confirmReleased(i, st);
          }
        },
        conflict: (ids) => this.onConflict(ids),
        capacity: (z) => this.onCapacity(z),
        gone: (r) => this.onGone(r),
        error: (e, op) => this.onSyncError(e, op),
        pending: () => this.onPending(),
      },
      { localOnly: !this.holdOnSelect },
    );
    this.syncLock();

    const map = (this.map = new MapView({
      model,
      label: t.map,
      describedBy: "help",
      seatLabel: (i) => this.seatAria(i, av.visual(i)),
      zoneLabel: (zi) => this.zoneAria(zi),
      reduceMotion,
      cb: {
        activate: (n, via) => this.activate(n, via),
        hover: (n, x, y) => this.onHover(n, x, y),
        focus: (n, kbd) => this.onNodeFocus(n, kbd),
        blur: () => this.hideTip(),
        escape: () => this.escape(),
        backgroundTap: () => this.closePop(),
        viewChange: () => this.onViewChange(),
        wheelHint: () => this.showHint(t.hintWheel(isMac() ? "⌘" : "Ctrl")),
      },
    }));

    // Leyenda de categorías (resalta al pulsar)
    const top = h("div", { class: "top", role: "group", "aria-label": t.categories });
    const used = new Set<number>([...model.rows.map((r) => r.cat), ...model.zones.map((z) => z.cat)]);
    this.catBtns = [];
    model.cats.forEach((c, ci) => {
      if (!used.has(ci)) return;
      const p = cfg.pricing[c.key];
      const b = h(
        "button",
        {
          type: "button",
          class: "cat",
          "aria-pressed": "false",
          "aria-label": `${t.highlight(c.label)}${p ? `, ${formatMoney(this.locale, p.price, p.currency, true)}` : ""}`,
        },
        h("span", { class: "sw" }),
        h("b", undefined, c.label),
        p ? h("span", { class: "pr" }, formatMoney(this.locale, p.price, p.currency)) : null,
      );
      b.querySelector<HTMLElement>(".sw")!.style.setProperty("--c", c.color);
      b.addEventListener("click", () => this.setHighlight(this.hl === ci ? -1 : ci));
      top.appendChild(b);
      this.catBtns[ci] = b;
    });

    // Mapa con sus capas flotantes
    this.mapBox = h("div", { class: "map" });
    this.tip = h("div", { class: "tip", "aria-hidden": "true" });
    this.hint = h("div", { class: "hint", "aria-hidden": "true" });
    this.noteIcon = h("span");
    this.noteText = h("span", { class: "nt", "aria-hidden": "true" });
    const dismiss = h("button", { type: "button", "aria-label": t.dismiss }, icon("close"));
    dismiss.addEventListener("click", () => this.hideNote());
    this.note = h("div", { class: "note" }, this.noteIcon, this.noteText, dismiss);
    this.pop = h("div", { class: "pop", role: "dialog", "aria-modal": "false", hidden: true });
    this.pop.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        this.escape();
      }
    });

    this.zIn = h("button", { type: "button", class: "ib", "aria-label": t.zoomIn, title: t.zoomIn }, icon("plus"));
    this.zOut = h("button", { type: "button", class: "ib", "aria-label": t.zoomOut, title: t.zoomOut }, icon("minus"));
    this.zReset = h("button", { type: "button", class: "ib", "aria-label": t.zoomReset, title: t.zoomReset }, icon("reset"));
    this.zIn.addEventListener("click", () => map.zoomBy(1.7));
    this.zOut.addEventListener("click", () => map.zoomBy(1 / 1.7));
    this.zReset.addEventListener("click", () => map.reset());
    const ctl = h("div", { class: "ctl", role: "group", "aria-label": t.map }, this.zIn, this.zOut, this.zReset);
    this.mapBox.append(map.el, this.tip, this.hint, ctl, this.note, this.pop);

    const help = h("p", { id: "help", class: "sr" }, t.mapHelp);

    // Leyenda de estados
    const keys = h("ul", { class: "keys", "aria-label": t.legendTitle });
    const keyItems: Array<[number, string, boolean]> = [
      [FREE, t.legendFree, false],
      [MINE, t.legendMine, false],
      [SOLD, t.legendSold, false],
      [HELD, t.legendHeld, false],
      [BLOCKED, t.legendBlocked, false],
    ];
    if (model.seats.some((x) => x.accessible)) keyItems.push([FREE, t.legendAccessible, true]);
    for (const it of keyItems) keys.appendChild(h("li", undefined, this.keyIcon(it[0], it[2]), it[1]));

    // Barra inferior: selección + temporizador
    this.sum = h("div", { class: "sum" });
    this.sy = h("span", { class: "sy", "aria-hidden": "true" }, t.syncing);
    this.clk = h("span", { role: "timer", "aria-live": "off" }, "00:00");
    this.extendBtn = h("button", { type: "button", class: "btn", hidden: true }, t.extend);
    this.extendBtn.addEventListener("click", () => void this.extendHold().catch(() => undefined));
    this.tm = h("div", { class: "tm", hidden: true }, h("span", { class: "cl" }, icon("clock"), h("small", undefined, t.holdTime), this.clk), this.extendBtn);
    const bar = h("div", { class: "bar" }, h("div", undefined, this.sum), this.tm);

    this.show(top, this.mapBox, help, keys, bar, this.live, this.alert);

    // Estado inicial
    for (let i = 0; i < model.seats.length; i++) if (av.visual(i) !== FREE) this.paintSeat(i);
    model.zones.forEach((_, zi) => this.paintZone(zi));
    map.setCurrent(map.firstNode((i) => av.visual(i) === FREE));
    this.renderBar();

    // Tamaño responsivo
    const fit = (width: number) => {
      if (width < 60 || width === this.lastW) return;
      this.lastW = width;
      this.w.toggleAttribute("data-narrow", width < 520);
      map.resize(width);
    };
    fit(this.mapBox.clientWidth || this.w.clientWidth);
    if (typeof ResizeObserver === "function") {
      const ro = new ResizeObserver((entries) => {
        const e = entries[0];
        if (e) fit(Math.floor(e.contentRect.width));
      });
      ro.observe(this.mapBox);
      this.cleanups.push(() => ro.disconnect());
    }

    // Esc en cualquier parte del widget
    this.w.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !e.defaultPrevented && this.escape()) e.preventDefault();
    });

    // Disponibilidad en vivo
    this.poller = new Poller(
      () => this.pollRun(),
      {
        setTimeout: (fn, ms) => setTimeout(fn, ms),
        clearTimeout: (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
        visible: () => typeof document === "undefined" || document.visibilityState !== "hidden",
        rand: Math.random,
      },
      (ok, errors) => this.onPollResult(ok, errors),
    );
    this.poller.start();
    const onVis = () => {
      if (document.visibilityState === "visible") {
        this.tick();
        this.poller?.resume();
      }
    };
    const onOnline = () => this.poller?.poke();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("online", onOnline);
    this.cleanups.push(() => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("online", onOnline);
    });
    this.tickTimer = setInterval(() => this.tick(), 1000);

    this.onViewChange();
    if (typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches) this.later(() => this.showHint(t.hintTouch, 3800), 700);

    this.o.onReady?.({
      eventId: cfg.eventId,
      name: cfg.name,
      chartName: model.name,
      seatCategories: model.cats.filter((_, ci) => model.rows.some((r) => r.cat === ci)).map((c) => c.key),
      startsAt: cfg.startsAt,
      seatCount: model.seats.length,
      holdTtlSec: cfg.holdTtlSec,
      pricing: cfg.pricing,
      categories: model.cats.map((c) => ({ key: c.key, label: c.label, color: c.color, price: cfg.pricing[c.key]?.price ?? null, currency: cfg.pricing[c.key]?.currency ?? null })),
      serverTime: cfg.serverTime,
    });

    if (restored) {
      this.sync!.restore(restored); // onHold + onSelectionChange: el portal necesita el token para cobrar
      this.notify("info", t.recovered);
    }
  }

  private keyIcon(vis: number, accessible: boolean): SVGSVGElement {
    const m = this.model!;
    const r = 7;
    const svg = s("svg", { viewBox: "-10 -10 20 20", width: 20, height: 20, "aria-hidden": "true", focusable: "false" });
    const cls = ["free", "sold", "held", "blocked", "mine"][vis]!;
    const c = s("circle", { class: `s ${cls}`, r, cx: 0, cy: 0, style: undefined });
    c.style.setProperty("--c", m.cats[0]!.color);
    c.style.cursor = "default";
    svg.appendChild(c);
    const kind = accessible ? "access" : vis === MINE ? "check" : vis === SOLD ? "cross" : vis === HELD ? "dots" : vis === BLOCKED ? "slash" : null;
    if (kind) {
      const p = s("path", { class: `mk ${kind}`, d: markPath(kind, 0, 0, r), "stroke-width": round1(kind === "dots" ? r * 0.36 : r * 0.24) });
      p.style.setProperty("--ink", m.cats[0]!.ink);
      svg.appendChild(p);
    }
    return svg;
  }

  private later(fn: () => void, ms: number): void {
    const id = setTimeout(() => {
      this.timers.delete(id);
      if (!this.destroyed) fn();
    }, ms);
    this.timers.add(id);
  }

  /* ------------------------------ etiquetas ------------------------------- */

  private stateText(vis: number): string {
    const t = this.t;
    return vis === MINE ? t.stMine : vis === SOLD ? t.stSold : vis === HELD ? t.stHeld : vis === BLOCKED ? t.stBlocked : t.stFree;
  }

  private priceOf(catKey: string): { price: number; currency: string } | null {
    return this.cfg?.pricing[catKey] ?? null;
  }

  private seatAria(i: number, vis: number): string {
    const m = this.model!;
    const sd = m.seats[i]!;
    const row = m.rows[sd.row]!;
    const cat = m.cats[sd.cat]!;
    const p = this.priceOf(cat.key);
    return this.t.seatAria(row.sectionLabel, row.label, sd.label, [cat.label, p ? formatMoney(this.locale, p.price, p.currency, true) : "", sd.accessible ? this.t.accessible : "", this.stateText(vis)]);
  }

  private zoneState(zi: number): { free: number; mine: number; max: number } {
    const m = this.model!;
    const z = m.zones[zi]!;
    const mine = this.sync!.gaQty(z.id);
    const base = this.av!.gaFree(z.id, z.capacity, this.sync!.confirmedGaQty(z.id));
    return { free: Math.max(0, base - mine), mine, max: base };
  }

  private zoneAria(zi: number): string {
    const m = this.model!;
    const z = m.zones[zi]!;
    const cat = m.cats[z.cat]!;
    const p = this.priceOf(cat.key);
    const st = this.zoneState(zi);
    return this.t.zoneAria(z.label, cat.label, p ? formatMoney(this.locale, p.price, p.currency, true) : "", st.free, st.mine);
  }

  private paintSeat(i: number): void {
    const vis = this.av!.visual(i);
    const pending = vis === MINE && !this.sync!.confirmedSeats.has(this.model!.seats[i]!.id);
    this.map!.paintSeat(i, vis, pending && !this.sync!.local, this.seatAria(i, vis));
  }

  private paintZone(zi: number): void {
    const st = this.zoneState(zi);
    const out = st.free + st.mine <= 0;
    this.map!.paintZone(zi, {
      cls: st.mine > 0 ? "mine" : out ? "out" : "free",
      sub: out && st.mine === 0 ? this.t.zoneSoldOut : this.t.zoneFree(st.free),
      qty: st.mine,
      label: this.zoneAria(zi),
    });
  }

  /* ------------------------------ avisos ---------------------------------- */

  private announce(text: string, assertive = false): void {
    const el = assertive ? this.alert : this.live;
    el.textContent = "";
    this.later(() => (el.textContent = text), 40);
  }

  private notify(kind: Kind, text: string, announce = true): void {
    if (!this.note) return;
    clear(this.noteIcon);
    this.noteIcon.appendChild(icon(kind === "info" ? "info" : kind === "warn" ? "warn" : "alert"));
    this.noteText.textContent = text;
    this.note.classList.add("on");
    if (this.noteTimer) clearTimeout(this.noteTimer);
    this.noteTimer = kind === "error" ? null : setTimeout(() => this.hideNote(), kind === "warn" ? 9000 : 5500);
    if (announce) this.announce(text, kind === "error");
  }

  private hideNote(): void {
    this.note?.classList.remove("on");
    if (this.noteTimer) clearTimeout(this.noteTimer);
    this.noteTimer = null;
  }

  private showHint(text: string, ms = 1500): void {
    const now = Date.now();
    if (now - this.hintAt < 2500) return;
    this.hintAt = now;
    this.hint.textContent = text;
    this.hint.classList.add("on");
    this.later(() => this.hint.classList.remove("on"), ms);
  }

  /** Esc: cierra lo último que se abrió. Devuelve true si cerró algo. */
  private escape(): boolean {
    if (this.popZone >= 0) {
      const n = this.model!.seats.length + this.popZone;
      this.closePop();
      this.map?.focusNode(n, true);
      return true;
    }
    if (this.tipVisible) {
      this.hideTip();
      return true;
    }
    if (this.note?.classList.contains("on")) {
      this.hideNote();
      return true;
    }
    return false;
  }

  /* ------------------------------ tooltip --------------------------------- */

  private tipContent(n: number): { l1: string; l2: string } {
    const m = this.model!;
    const t = this.t;
    if (n < m.seats.length) {
      const sd = m.seats[n]!;
      const row = m.rows[sd.row]!;
      const cat = m.cats[sd.cat]!;
      const p = this.priceOf(cat.key);
      const vis = this.av!.visual(n);
      const parts = [cat.label, p ? formatMoney(this.locale, p.price, p.currency) : "", sd.accessible ? t.accessible : "", vis === FREE ? "" : this.stateText(vis)].filter(Boolean);
      return { l1: t.seatTip(row.sectionLabel, row.label, sd.label), l2: parts.join(" · ") };
    }
    const zi = n - m.seats.length;
    const z = m.zones[zi]!;
    const cat = m.cats[z.cat]!;
    const p = this.priceOf(cat.key);
    const st = this.zoneState(zi);
    return { l1: z.label, l2: [cat.label, p ? `${formatMoney(this.locale, p.price, p.currency)} ${t.zoneEach}` : "", st.free + st.mine > 0 ? t.zoneFree(st.free) : t.zoneSoldOut].filter(Boolean).join(" · ") };
  }

  private showTip(n: number, x: number, y: number, lift: number): void {
    if (this.popZone >= 0) return;
    const tc = this.tipContent(n);
    clear(this.tip);
    this.tip.append(h("b", undefined, tc.l1), h("span", undefined, tc.l2));
    const W = this.mapBox.clientWidth;
    const H = this.mapBox.clientHeight;
    const tw = this.tip.offsetWidth;
    const th = this.tip.offsetHeight;
    let left = Math.min(Math.max(6, x - tw / 2), Math.max(6, W - tw - 6));
    let top = y - th - lift;
    if (top < 6) top = Math.min(H - th - 6, y + lift);
    left = Math.round(left);
    top = Math.round(top);
    this.tip.style.transform = `translate(${left}px, ${top}px)`;
    this.tip.classList.add("on");
    this.tipVisible = true;
  }

  private hideTip(): void {
    if (!this.tipVisible) return;
    this.tip.classList.remove("on");
    this.tipVisible = false;
  }

  private onHover(n: number, x: number, y: number): void {
    if (n < 0) return this.hideTip();
    this.showTip(n, x, y, 16);
  }

  private onNodeFocus(n: number, kbd: boolean): void {
    if (!kbd || !this.map) return;
    const p = this.map.nodePos(n);
    this.showTip(n, p.x, p.y, this.map.nodeRadius(n) + 10);
  }

  /* ------------------------------ interacción ----------------------------- */

  private activate(n: number, via: "pointer" | "key" | "ax"): void {
    if (!this.model) return;
    if (n < this.model.seats.length) this.toggleSeat(n);
    else this.openZone(n - this.model.seats.length, via === "key");
  }

  private setHighlight(ci: number): void {
    this.hl = ci;
    this.catBtns.forEach((b, i) => b?.setAttribute("aria-pressed", String(i === ci)));
    this.map?.highlight(ci);
  }

  private toggleSeat(i: number): void {
    const m = this.model!;
    const sync = this.sync!;
    const id = m.seats[i]!.id;
    const vis = this.av!.visual(i);
    this.closePop();
    if (vis === MINE) {
      sync.removeSeat(id);
      this.announce(this.t.deselectedAnn(this.seatName(i), sync.count()));
      return;
    }
    if (vis !== FREE) {
      this.announce(this.t.notAvailableAnn(this.seatName(i)));
      return;
    }
    if (sync.count() + 1 > this.max) {
      this.notify("info", this.t.maxReached(this.max));
      return;
    }
    if (!sync.addSeat(id)) return; // sesión del portal ausente o caducada: ya se avisó, sin selección fantasma ni anuncio de "seleccionada"
    const sel = this.buildSel();
    this.announce(this.t.selectedAnn(this.seatName(i), sync.count(), sel.total ? formatMoney(this.locale, sel.total.amount, sel.total.currency, true) : ""));
  }

  private seatName(i: number): string {
    const sd = this.model!.seats[i]!;
    const row = this.model!.rows[sd.row]!;
    return this.t.seatTip(row.sectionLabel, row.label, sd.label);
  }

  /* --------------------------- zona de entrada general -------------------- */

  private closePop(): void {
    if (this.popZone < 0) return;
    this.popZone = -1;
    this.pop.hidden = true;
    clear(this.pop);
  }

  private openZone(zi: number, byKeyboard: boolean): void {
    const m = this.model!;
    const sync = this.sync!;
    const t = this.t;
    const z = m.zones[zi]!;
    const cat = m.cats[z.cat]!;
    const p = this.priceOf(cat.key);
    if (this.popZone === zi) return this.closePop();
    this.closePop();
    this.hideTip();

    const st = this.zoneState(zi);
    if (st.free + st.mine <= 0) {
      this.announce(t.notAvailableAnn(z.label));
      return;
    }
    const cap = () => gaMaxQty(st.max, sync.count(), st.mine, this.max);
    const maxQty = cap();
    let qty = st.mine > 0 ? st.mine : Math.min(1, maxQty);

    const title = h("h3", { id: "pop-t" }, z.label);
    const sub = h("p", undefined, [cat.label, p ? `${formatMoney(this.locale, p.price, p.currency)} ${t.zoneEach}` : ""].filter(Boolean).join(" · "));
    const input = h("input", { type: "number", id: "pop-q", min: 0, max: maxQty, value: qty, inputmode: "numeric", "aria-describedby": "pop-m" });
    const dec = h("button", { type: "button", "aria-label": t.qtyDec }, "−");
    const inc = h("button", { type: "button", "aria-label": t.qtyInc }, "+");
    const mx = h("span", { class: "mx", id: "pop-m" }, t.zoneLimit(maxQty));
    const row = h("div", { class: "step" }, dec, input, inc);
    const apply = h("button", { type: "button", class: "btn" });
    const remove = h("button", { type: "button", class: "btn sec2" }, t.zoneRemove);
    const close = h("button", { type: "button", class: "btn sec2" }, t.close);
    const acts = h("div", { class: "acts" }, apply, st.mine > 0 ? remove : null, close);

    const refresh = () => {
      qty = clampQty(qty, maxQty);
      input.value = String(qty);
      dec.disabled = qty <= 0;
      inc.disabled = qty >= maxQty;
      apply.disabled = qty === st.mine || (qty === 0 && st.mine === 0);
      apply.textContent = qty === 0 ? t.zoneRemove : st.mine > 0 ? t.zoneUpdate(qty) : t.zoneAdd(qty);
    };
    dec.addEventListener("click", () => {
      qty--;
      refresh();
    });
    inc.addEventListener("click", () => {
      qty++;
      refresh();
    });
    input.addEventListener("input", () => {
      qty = Number(input.value) || 0;
      qty = clampQty(qty, maxQty);
      apply.disabled = qty === st.mine || (qty === 0 && st.mine === 0);
      apply.textContent = qty === 0 ? t.zoneRemove : st.mine > 0 ? t.zoneUpdate(qty) : t.zoneAdd(qty);
    });
    input.addEventListener("change", refresh);
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !apply.disabled) {
        e.preventDefault();
        apply.click();
      }
    });
    const done = (newQty: number) => {
      const n = m.seats.length + zi;
      this.closePop();
      if (sync.setGa(z.id, newQty)) this.announce(newQty > 0 ? t.gaSetAnn(z.label, newQty) : t.gaRemovedAnn(z.label));
      this.map?.focusNode(n, byKeyboard);
    };
    apply.addEventListener("click", () => done(qty));
    remove.addEventListener("click", () => done(0));
    close.addEventListener("click", () => this.escape());

    this.pop.setAttribute("aria-labelledby", "pop-t");
    this.pop.append(title, sub, h("label", { for: "pop-q" }, t.qty, mx), row, acts);
    refresh();
    this.pop.hidden = false;
    this.popZone = zi;

    // Posición: junto a la zona, dentro del mapa
    const pos = this.map!.nodePos(m.seats.length + zi);
    const rad = this.map!.nodeRadius(m.seats.length + zi);
    const W = this.mapBox.clientWidth;
    const H = this.mapBox.clientHeight;
    const pw = this.pop.offsetWidth;
    const ph = this.pop.offsetHeight;
    const left = Math.min(Math.max(8, pos.x - pw / 2), Math.max(8, W - pw - 8));
    let top = pos.y + rad + 10; // debajo de la zona; si no cabe, encima; si tampoco, debajo (puede asomar del mapa)
    if (top + ph > H - 8 && pos.y - rad - 10 - ph >= 8) top = pos.y - rad - 10 - ph;
    top = Math.max(8, top);
    this.pop.style.left = `${Math.round(left)}px`;
    this.pop.style.top = `${Math.round(top)}px`;
    input.focus();
    input.select();
  }

  /* ------------------------------ selección ------------------------------- */

  private buildSel(): Selection {
    if (!this.model || !this.sync || !this.cfg) return { seats: [], ga: [], total: null, pending: false };
    return buildSelection(this.model, this.sync.desiredSeats, this.sync.desiredGa, this.cfg.pricing, this.sync.isPending() && !this.sync.local);
  }

  /** Refleja `sync.desired` en el mapa, la barra y el portal. */
  private afterSelection(): void {
    if (!this.model || !this.av || !this.sync) return;
    const m = this.model;
    const av = this.av;
    const want = new Set<number>();
    for (const id of this.sync.desiredSeats) {
      const i = m.index.get(id);
      if (i !== undefined) want.add(i);
    }
    const touched = new Set<number>();
    for (const i of [...av.mine]) {
      if (!want.has(i)) {
        av.setMine(i, false, this.serverNow());
        touched.add(i);
      }
    }
    for (const i of want) {
      if (!av.mine.has(i)) {
        av.setMine(i, true, this.serverNow());
        touched.add(i);
      }
    }
    for (const i of touched) this.paintSeat(i);
    m.zones.forEach((_, zi) => this.paintZone(zi));
    this.renderBar();
    this.emitSelection();
  }

  private emitSelection(force = false): void {
    const sel = this.buildSel();
    const sig = JSON.stringify([sel.seats.map((x) => x.id), sel.ga.map((g) => [g.zoneId, g.qty]), sel.pending, sel.total]);
    if (!force && sig === this.lastSig) return;
    this.lastSig = sig;
    try {
      this.o.onSelectionChange?.(sel);
    } catch (e) {
      this.reportCallbackError(e);
    }
  }

  private reportCallbackError(e: unknown): void {
    // Un fallo en el código del portal no debe romper el widget.
    setTimeout(() => {
      throw e;
    });
  }

  private onPending(): void {
    const p = this.sync!.isPending() && !this.sync!.local;
    this.sy?.classList.toggle("on", p);
    // los asientos pendientes se dibujan con anillo discontinuo hasta que el servidor confirma
    for (const i of this.av!.mine) this.paintSeat(i);
    this.renderBar();
    this.emitSelection();
  }

  /* ------------------------------ bloqueo / temporizador ------------------ */

  private holdInfo(): HoldInfo | null {
    const hd = this.sync?.hold;
    if (!hd || !this.sync) return null;
    return {
      holdToken: hd.token,
      expiresAt: hd.expiresAt,
      expiresAtLocal: hd.expiresAt - this.offset,
      seats: [...this.sync.confirmedSeats],
      ga: [...this.sync.confirmedGa].map((e) => ({ zoneId: e[0], qty: e[1] })),
    };
  }

  private onHoldChanged(hd: HoldState | null): void {
    this.persist(hd);
    if (!this.av) return;
    for (const i of this.av.mine) this.paintSeat(i);
    this.model!.zones.forEach((_, zi) => this.paintZone(zi));
    if (hd) {
      if (hd.expiresAt > this.warnedExpiry) this.warnedExpiry = 0;
      const info = this.holdInfo();
      if (info) {
        try {
          this.o.onHold?.(info);
        } catch (e) {
          this.reportCallbackError(e);
        }
      }
    }
    this.renderTimer();
  }

  private remaining(): number | null {
    const hd = this.sync?.hold;
    return hd ? hd.expiresAt - this.serverNow() : null;
  }

  private renderTimer(): void {
    const rem = this.remaining();
    if (rem === null) {
      this.tm.hidden = true;
      this.tm.classList.remove("low");
      this.extendBtn.hidden = true;
      return;
    }
    this.tm.hidden = false;
    this.clk.textContent = formatClock(rem);
    const low = rem < 60_000;
    this.tm.classList.toggle("low", low);
    this.extendBtn.hidden = !low;
    this.extendBtn.disabled = this.extending;
  }

  private tick(): void {
    if (this.destroyed || !this.av) return;
    for (const i of this.av.tick(this.serverNow())) this.paintSeat(i);
    const rem = this.remaining();
    if (rem === null) return;
    if (rem <= 0) {
      this.sync!.expireLocal();
      return;
    }
    if (rem < 60_000 && this.warnedExpiry !== this.sync!.hold!.expiresAt) {
      this.warnedExpiry = this.sync!.hold!.expiresAt;
      this.notify("warn", this.t.minuteLeft);
    }
    this.renderTimer();
  }

  extendHold(): Promise<HoldInfo> {
    const sync = this.sync;
    if (!sync || !sync.hold) return Promise.reject(new ApiError("no_hold", "No hay ningún bloqueo activo"));
    this.extending = true;
    this.extendBtn.textContent = this.t.extending;
    this.renderTimer();
    return sync
      .extend()
      .then((r) => {
        const info = this.holdInfo()!;
        const rem = this.remaining() ?? 0;
        this.warnedExpiry = info.expiresAt; // ya se avisó: no repetir el aviso de "menos de un minuto" encima del de "ampliado"
        if (r.extended) this.notify("info", this.t.extended(formatClock(rem)));
        else this.notify("warn", this.t.extendMax);
        return info;
      })
      .catch((e) => {
        const err = toApiError(e);
        if (!["hold_expired", "hold_not_active", "hold_lost", "not_found"].includes(err.code)) {
          this.notify("error", this.t.extendFailed);
          this.o.onError?.(toWidgetError(err));
        }
        throw err;
      })
      .finally(() => {
        this.extending = false;
        this.extendBtn.textContent = this.t.extend;
        this.renderTimer();
      });
  }

  /* ------------------------------ eventos del sync ------------------------ */

  private onConflict(ids: string[]): void {
    const m = this.model!;
    const av = this.av!;
    const names: string[] = [];
    for (const id of ids) {
      const i = m.index.get(id);
      if (i === undefined) continue;
      av.force(i, SOLD, this.serverNow());
      this.paintSeat(i);
      names.push(this.seatName(i));
    }
    this.afterSelection();
    const text = names.length === 1 ? this.t.seatLost(names[0]!) : this.t.seatsLost(names.length);
    if (names.length) this.notify("warn", text);
    this.o.onError?.({ code: "seat_unavailable", message: text, status: 409, details: { seats: ids }, retryable: false, recovered: true });
    this.forceFresh = true;
    this.poller?.poke();
  }

  private onCapacity(zoneId: string): void {
    const zi = this.model!.zoneIndex.get(zoneId);
    const label = zi === undefined ? zoneId : this.model!.zones[zi]!.label;
    this.notify("warn", this.t.capacityLost(label));
    this.o.onError?.({ code: "capacity_exceeded", message: this.t.capacityLost(label), status: 409, details: { zone: zoneId }, retryable: false, recovered: true });
    this.forceFresh = true;
    this.poller?.poke();
  }

  private onGone(reason: "expired" | "booked"): void {
    this.afterSelection();
    this.closePop();
    this.renderTimer();
    if (reason === "booked") {
      this.announce(this.t.holdBooked);
    } else {
      this.notify("warn", this.t.holdExpired);
      try {
        this.o.onHoldExpired?.();
      } catch (e) {
        this.reportCallbackError(e);
      }
    }
    this.forceFresh = true;
    this.poller?.poke();
  }

  /**
   * Bloqueo con sesión: si el evento exige token del portal y no hay `sessionToken`, el widget no intenta bloquear
   * (el primer clic ya muestra el aviso y emite `onError`, ahorrando la petición). `setSessionToken` lo levanta.
   */
  private syncLock(): void {
    if (this.sync) this.sync.lock = this.holdOnSelect && this.cfg?.requireSession === true && !this.sessionToken ? new ApiError("session_required", this.t.sessionRequired, 403) : null;
  }

  private onSyncError(err: ApiError, op: SyncOp): void {
    const t = this.t;
    const notice = blockNotice(t, err.code, err.details);
    if (notice) {
      // Cuota o sesión: aviso visible + aria-live, sin marcar butacas como ocupadas ni forzar refresco (no cambió nada ahí).
      const quota = err.code === "quota_exceeded";
      this.notify(quota ? "warn" : "error", notice);
      if (quota || !this.lockReported) {
        if (!quota) this.lockReported = true; // el aviso de sesión se repite en cada clic, pero `onError` solo una vez
        this.o.onError?.({ ...toWidgetError(err, quota), message: notice });
      }
      return;
    }
    const text =
      err.code === "invalid_request" && op === "place" && /zonas/i.test(err.message)
        ? t.zonesLimit
        : op === "remove"
          ? t.removeError
          : t.saveError;
    this.notify("error", text);
    this.o.onError?.(toWidgetError(err));
    this.forceFresh = true;
    this.poller?.poke();
  }

  /* ------------------------------ disponibilidad -------------------------- */

  private async pollRun(): Promise<void> {
    const fresh = this.forceFresh;
    this.forceFresh = false;
    const snap = await this.api.snapshot(this.cfg!.availabilityUrl, fresh);
    if (this.destroyed) return;
    // Foto inexistente o ilegible: no es un error ni hay backoff; se conserva el estado que ya teníamos.
    if (snap) this.applySnapshot(snap);
  }

  private applySnapshot(snap: Snapshot): void {
    const av = this.av!;
    const m = this.model!;
    const res = av.apply(snap, this.serverNow());
    if (res.stale) return;
    for (const i of res.changed) this.paintSeat(i);
    for (const id of res.gaChanged) {
      const zi = m.zoneIndex.get(id);
      if (zi !== undefined) this.paintZone(zi);
    }
    // ¿Alguna de mis butacas confirmadas aparece vendida? (p. ej. el portal acaba de cobrar, o la perdí)
    const sync = this.sync!;
    if (!this.verifying && !sync.local && sync.hold) {
      const lost: string[] = [];
      for (const i of av.mine) {
        const id = m.seats[i]!.id;
        const b = av.base[i];
        if ((b === SOLD || b === BLOCKED) && sync.confirmedSeats.has(id)) lost.push(id);
      }
      if (lost.length) {
        this.verifying = true;
        void sync.verify(lost).finally(() => (this.verifying = false));
      }
    }
  }

  private onPollResult(ok: boolean, errors: number): void {
    if (!ok && errors >= 2 && !this.offline) {
      this.offline = true;
      this.notify("warn", this.t.netLost);
    } else if (ok && this.offline) {
      this.offline = false;
      this.hideNote();
      this.announce(this.t.netBack);
    }
  }

  /* ------------------------------ vista ----------------------------------- */

  private onViewChange(): void {
    const map = this.map;
    if (!map || !this.zIn) return;
    this.closePop();
    this.hideTip();
    this.zOut.disabled = !map.zoomed;
    this.zReset.disabled = !map.zoomed;
    this.zIn.disabled = map.atMax;
  }

  /* ------------------------------ barra ----------------------------------- */

  private renderBar(): void {
    if (!this.model || !this.sum) return;
    const t = this.t;
    const m = this.model;
    const sel = this.buildSel();
    const n = sel.seats.length + sel.ga.reduce((a, g) => a + g.qty, 0);
    const hadFocus = this.sum.contains(this.shadow.activeElement);
    const focusIdx = hadFocus ? [...this.sum.querySelectorAll("button")].indexOf(this.shadow.activeElement as HTMLButtonElement) : -1;
    clear(this.sum);
    if (n === 0) {
      this.sum.appendChild(h("div", { class: "em2" }, t.barEmpty(this.max)));
      this.sum.appendChild(this.sy);
      if (hadFocus) this.map?.focusNode(this.map.currentNode, true);
      return;
    }
    const totalTxt = sel.total ? formatMoney(this.locale, sel.total.amount, sel.total.currency) : "";
    this.sum.appendChild(h("div", { class: "sm" }, t.tickets(n), totalTxt ? h("span", undefined, ` · ${totalTxt}`) : null, this.sy));
    const ul = h("ul", { class: "chips" });
    for (const sd of sel.seats) {
      const i = m.index.get(sd.id)!;
      const li = h("li", { class: "chp" }, h("i"), `${sd.sectionLabel} ${sd.rowLabel}-${sd.label}`);
      li.querySelector<HTMLElement>("i")!.style.setProperty("--c", m.cats[m.seats[i]!.cat]!.color);
      const b = h("button", { type: "button", "aria-label": t.removeChip(this.seatName(i)) }, icon("close"));
      b.addEventListener("click", () => {
        this.sync!.removeSeat(sd.id);
        this.announce(t.deselectedAnn(this.seatName(i), this.sync!.count()));
      });
      li.appendChild(b);
      ul.appendChild(li);
    }
    for (const g of sel.ga) {
      const zi = m.zoneIndex.get(g.zoneId)!;
      const li = h("li", { class: "chp" }, h("i"), `${g.label} ×${g.qty}`);
      li.querySelector<HTMLElement>("i")!.style.setProperty("--c", m.cats[m.zones[zi]!.cat]!.color);
      const b = h("button", { type: "button", "aria-label": t.removeChip(g.label) }, icon("close"));
      b.addEventListener("click", () => {
        this.sync!.setGa(g.zoneId, 0);
        this.announce(t.gaRemovedAnn(g.label));
      });
      li.appendChild(b);
      ul.appendChild(li);
    }
    this.sum.appendChild(ul);
    if (hadFocus) {
      const btns = this.sum.querySelectorAll("button");
      const target = btns[Math.min(Math.max(focusIdx, 0), btns.length - 1)] as HTMLButtonElement | undefined;
      target?.focus();
    }
  }

  /* ------------------------------ API pública ----------------------------- */

  getSelection(): Selection {
    return this.buildSel();
  }

  getHold(): HoldInfo | null {
    return this.holdInfo();
  }

  clear(): Promise<void> {
    return this.sync ? this.sync.clear() : Promise.resolve();
  }

  release(): Promise<void> {
    return this.clear();
  }

  whenSynced(): Promise<void> {
    return this.sync ? this.sync.flush() : Promise.resolve();
  }

  setSessionToken(token: string): void {
    if (token !== "" && !isSessionToken(token)) throw new TypeError("setSessionToken: token no válido");
    this.sessionToken = token || undefined;
    this.lockReported = false;
    this.syncLock();
    const shown = this.noteText?.textContent;
    if (shown === this.t.sessionInvalid || shown === this.t.sessionRequired) this.hideNote();
  }

  getSessionId(): string {
    return this.sessionId;
  }

  async refresh(): Promise<void> {
    if (!this.cfg || this.destroyed) return;
    const snap = await this.api.snapshot(this.cfg.availabilityUrl, true);
    if (!this.destroyed && snap) this.applySnapshot(snap);
  }

  async selectBest(count: number, category?: string): Promise<SeatSel[]> {
    const sync = this.sync;
    const m = this.model;
    if (!sync || !m || !this.av) throw new ApiError("not_ready", "El mapa aún no está listo");
    const n = Math.floor(count);
    if (!Number.isFinite(n) || n < 1) throw new ApiError("invalid_request", "count debe ser un entero >= 1", 400);
    if (sync.count() + n > this.max) {
      this.notify("info", this.t.maxReached(this.max));
      throw new ApiError("max_selectable", this.t.maxReached(this.max), 0);
    }
    let ids: string[];
    if (sync.local) {
      const picked = pickBestLocal(m, (i) => this.av!.visual(i) === FREE, n, category);
      if (!picked) {
        this.notify("info", this.t.noBest(n));
        throw new ApiError("seat_unavailable", this.t.noBest(n), 409, { seats: [] });
      }
      ids = picked.map((i) => m.seats[i]!.id);
      for (const id of ids) sync.addSeat(id);
    } else {
      try {
        ids = await sync.selectBest(n, category);
      } catch (e) {
        const err = toApiError(e);
        if (err.code === "seat_unavailable") this.notify("info", this.t.noBest(n));
        else if (blockNotice(this.t, err.code)) this.onSyncError(err, "best");
        throw err;
      }
    }
    this.notify("info", this.t.bestDone(ids.length));
    const set = new Set(ids);
    return this.buildSel().seats.filter((x) => set.has(x.id));
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.life.abort();
    this.poller?.stop();
    this.sync?.destroy();
    this.map?.destroy();
    if (this.tickTimer) clearInterval(this.tickTimer);
    if (this.noteTimer) clearTimeout(this.noteTimer);
    for (const id of this.timers) clearTimeout(id);
    this.timers.clear();
    for (const fn of this.cleanups) fn();
    this.cleanups.length = 0;
    this.host.remove();
  }
}
